import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { putChangeProfile } from "../../Store/Action";
import ActionType from "../../Store/ActionTypes";
import { getWithExpiry } from "../../utils/token";
import { useThemeClass } from "@/Hooks/useThemeClass";
import { useTheme } from "styled-components";
import clsx from "clsx";

const { USER } = ActionType;

function Profile() {
  const themeClass = useThemeClass()
  const theme = useTheme()
  const dispatch = useDispatch();

  const dataUser = useSelector(
    (state) => state.ReducerloginUser.loginedUser.data
  );
  /* prettier-ignore */
  const userInfo = getWithExpiry("userInfo");

  const [formData, setFormData] = useState({
    name: userInfo?.name || "",
    nick_name: userInfo?.nick_name || "",
    phone: userInfo?.phone || "",
    blog_url: userInfo?.SnsUrl || "",
    user_id: userInfo?.id || 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dataUser) {
      setFormData({
        name: dataUser.name || "",
        nick_name: dataUser.nick_name || "",
        phone: dataUser.phone || "",
        blog_url: dataUser.SnsUrl || "",
        user_id: Number(dataUser.id) || 0,
      });
    }
  }, [dataUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateBlogUrl = (url) => {
    const regex = /^https:\/\/blog\.naver\.com\/[a-zA-Z0-9._-]+$/;
    return regex.test(url);
  };

  const handleBlogUrlBlur = () => {
    let url = formData.blog_url.trim();

    if (url && !url.startsWith("http")) {
      url = "https://" + url;
    }

    if (url === "https://blog.naver.com" || url === "https://blog.naver.com/") {
      Swal.fire({
        icon: "warning",
        title: "잘못된 URL",
        text: "예: https://blog.naver.com/아이디",
        confirmButtonText: "확인",
        buttonsStyling: false,
        customClass: {
          confirmButton: "bg-yellow-500 text-white px-6 py-2 rounded-full",
        },
      });
      return;
    }

    if (!validateBlogUrl(url)) {
      Swal.fire({
        icon: "error",
        title: "잘못된 URL",
        text: "예: https://blog.naver.com/아이디",
        confirmButtonText: "확인",
        buttonsStyling: false,
        customClass: {
          confirmButton: "bg-red-600 text-white px-6 py-2 rounded-full",
        },
      });
    }

    setFormData((prev) => ({
      ...prev,
      blog_url: url,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!validateBlogUrl(formData.blog_url)) {
        Swal.fire({
          icon: "error",
          title: "잘못된 블로그 주소입니다.",
          text: "예: https://blog.naver.com/아이디",
          confirmButtonText: "확인",
          buttonsStyling: false,
          customClass: {
            confirmButton: "bg-red-600 text-white px-6 py-2 rounded-full",
          },
        });
        setLoading(false);
        return;
      }

      const postData = {
        ...formData,
        blog_url: formData.blog_url,
      };

      const response = await dispatch(putChangeProfile(postData));

      if (response?.meta?.code === 200) {
        const updatedData = {
          ...dataUser,
          ...response.data,
          SnsUrl: formData.blog_url,
        };

        dispatch({
          type: USER.POST_LOGIN_SUCCESS,
          payload: { ...response, data: updatedData },
        });

        Swal.fire({
          icon: "success",
          title: "성공",
          text: "프로필이 성공적으로 업데이트되었습니다!",
          confirmButtonText: "확인",
          buttonsStyling: false,
          customClass: {
            confirmButton: "bg-green-600 text-white px-6 py-2 rounded-full",
          },
        });
      } else {
        throw new Error(
          response?.meta?.message || "프로필 업데이트에 실패했습니다."
        );
      }
    } catch (err) {
      setError(err.message || "오류가 발생했습니다.");
      Swal.fire({
        icon: "error",
        title: "오류",
        text: err.message || "프로필 업데이트에 실패했습니다.",
        confirmButtonText: "확인",
        buttonsStyling: false,
        customClass: {
          confirmButton: "bg-red-600 text-white px-6 py-2 rounded-full",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="flex flex-col gap-5 pt-4" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-3">
        <p className="text-sm">이름</p>
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`${clsx(themeClass.bg("background"))} rounded-md w-full py-3 px-3`}
          placeholder="입력 이름"
        />
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-sm">닉네임</p>
        <input
          name="nick_name"
          value={formData.nick_name}
          onChange={handleChange}
          className={`${clsx(themeClass.bg("background"))} rounded-md w-full py-3 px-3`}
          placeholder="입력 닉네임"
        />
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-sm">휴대폰번호</p>
        <input
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`${clsx(themeClass.bg("background"))} rounded-md w-full py-3 px-3`}
          placeholder="입력 휴대폰번호"
        />
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-sm">네이버 블로그 URL</p>
        <input
          name="blog_url"
          value={formData.blog_url}
          onChange={handleChange}
          onBlur={handleBlogUrlBlur}
          type="url"
          className={`${clsx(themeClass.bg("background"))} rounded-md w-full py-3 px-3`}
          placeholder="네이버 블로그 URL"
        />
      </div>
      {error && <p className="text-red-500 text-sm">오류: {error}</p>}
      <button
        type="submit"
        className={`${clsx(themeClass.bg("text"))} mt-5 py-3 text-white rounded-full text-sm`}
        disabled={loading}
      >
        {loading ? "수정 중..." : "수정하기"}
      </button>
    </form>
  );
}

export default Profile;
